"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, MapPinOff, TrendingDown, ShieldAlert, SearchX, Stethoscope, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const challengesData = [{
  icon: "MapPinOff",
  title: "Zero Google Map Pack Visibility",
  impact: "Invisible for high-intent 'dermatologist near me' and clinic queries across prime catchment postcodes.",
  diagnosis: "Inconsistent NAP citations, unverified secondary categories, and lack of localized geo-signals meant Google defaulted to hospital directories instead of the clinic."
}, {
  icon: "SearchX",
  title: "Cannibalised Clinical Search Intent",
  impact: "Generic informational pages ranked weakly while private consultation booking pages had no search footprint.",
  diagnosis: "Blog posts were competing with core procedure pages for queries like acne scarring treatments and mole removal, splitting page authority."
}, {
  icon: "ShieldAlert",
  title: "Weak Medical E-E-A-T Signals",
  impact: "Search engines suppressed treatment pages following Google's strict Your Money or Your Life (YMYL) updates.",
  diagnosis: "Missing GMC registration links, uncredited medical practitioner bios, and absent clinical schema markup caused search engines to doubt clinical authority."
}, {
  icon: "TrendingDown",
  title: "Fragmented Local Competitor Pressure",
  impact: "Aggregator platforms and corporate health chains dominated local treatment rankings.",
  diagnosis: "Competitors maintained structured local landing pages for adjacent towns, while this single-location practice lacked tailored location-based content hubs."
}];
const diagnosticChecklist = ["Incomplete Google Business Profile service menu configuration", "Zero MedicalWebPage schema markup on core procedure URLs", "Sub-par page load speeds on mobile booking forms (>4.2s LCP)", "Unindexed localized treatment reviews and verified clinical credentials"];
export default function Challenges() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const getIcon = (name: string) => {
    switch (name) {
      case "MapPinOff":
        return <MapPinOff className="size-5 text-destructive" />;
      case "SearchX":
        return <SearchX className="size-5 text-destructive" />;
      case "ShieldAlert":
        return <ShieldAlert className="size-5 text-destructive" />;
      case "TrendingDown":
        return <TrendingDown className="size-5 text-destructive" />;
      default:
        return <AlertTriangle className="size-5 text-destructive" />;
    }
  };
  return <section id="localsearchchallenges" data-nav="light" className="bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-14 md:mb-18">
          <div className="flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary bg-secondary">
              Case Study Diagnostic
            </Badge>
            <span className="text-xs font-mono text-muted-foreground">Private Healthcare SEO</span>
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold tracking-tight text-foreground mb-6">
            The Local Search Visibility Challenges Faced by the Practice
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
            When the lead dermatologist approached me, the practice had world-class clinical capabilities but was virtually invisible in organic search. Patients searching for urgent consultations in their immediate area were consistently routed to directory portals and competing private clinics.
          </p>
        </div>

        {/* Hurdles Grid */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-16" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }}>
          {challengesData.map((item, i) => <motion.div key={item.title} variants={itemVariants} data-index={i}>
              <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200">
                <CardHeader className="space-y-3 pb-3">
                  <div className="flex items-center justify-between">
                    <div className="p-2.5 rounded-lg bg-secondary/60 inline-flex items-center justify-center">
                      {getIcon(item.icon)}
                    </div>
                    <span className="text-xs font-mono text-muted-foreground uppercase">Hurdle 0{i + 1}</span>
                  </div>
                  <CardTitle className="text-xl font-serif font-bold text-card-foreground">
                    {item.title}
                  </CardTitle>
                  <CardDescription className="text-sm font-medium text-foreground/80 leading-snug">
                    {item.impact}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-2">
                  <Separator className="my-3 bg-border" />
                  <div className="space-y-1.5">
                    <span className="text-xs font-semibold uppercase tracking-wide text-primary">
                      Initial Audit Finding:
                    </span>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.diagnosis}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Diagnosis Takeaway Banner */}
        <div className="rounded-2xl border border-border bg-muted/40 p-6 sm:p-8 lg:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4">
              <div className="inline-flex items-center gap-2 text-primary font-medium text-sm">
                <Stethoscope className="size-4 text-primary" />
                <span>Technical &amp; Medical SEO Audit Summary</span>
              </div>
              <h3 className="text-2xl font-serif font-bold text-foreground">
                Why Standard Local Agency Tactics Had Failed
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                Generalist agencies applied generic local citations without solving medical intent mapping or YMYL compliance. In private healthcare, search engines evaluate authority with stringent precision. You cannot rank without verified clinician validation and spotless technical structure.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                {diagnosticChecklist.map((item, idx) => <div key={item} data-index={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </div>)}
              </div>
            </div>

            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col justify-center items-start lg:items-end gap-4 border-t lg:border-t-0 lg:border-l border-border pt-6 lg:pt-0 lg:pl-8">
              <div className="w-full text-left lg:text-right">
                <p className="text-xs font-mono text-muted-foreground uppercase">Next Stage</p>
                <p className="text-lg font-serif font-semibold text-foreground">How I Resolved These Hurdles</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Explore the full implementation strategy and verified patient appointment outcomes.
                </p>
              </div>

              <div className="flex flex-col w-full sm:w-auto gap-3">
                <Button asChild size="lg" className="w-full sm:w-auto justify-center font-medium">
                  <Link to="/CaseStudiesPage">
                    View Case Study Strategy
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="w-full sm:w-auto justify-center">
                  <Link to="/LocalSEOPage">
                    My Local SEO Services
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}